import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native'
import {Feather} from '@expo/vector-icons'
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome'
import {msg, plural} from '@lingui/core/macro'
import {useLingui} from '@lingui/react'
import {Trans} from '@lingui/react/macro'

import {
  type DocumentMaterial,
  type DocumentMaterialRef,
  type DocumentPageMaterial,
  materialPageCounts,
  pageMaterialFor,
} from '#/lib/document/material'
import {type OptimizeLevel} from '#/lib/document/optimize'
import {type PdfSource, prefetchPdfPage} from '#/lib/document/pdf.web'
import {PDF_SKELETON_CLASS, usePdfStyles} from '#/lib/document/pdfStyles.web'
import {
  type DocumentAudience,
  type DocumentEmbedView,
} from '#/lib/document/types'
import {useIsNearScreen} from '#/lib/document/useIsNearScreen'
import {usePdfDocument} from '#/lib/document/usePdfDocument.web'
import {buildDocumentBlobUrl, formatFileSize} from '#/lib/document/util'
import {getPdfDpr, useHasHover} from '#/lib/document/webEnv.web'
import {isTextEntry} from '#/lib/textEntry'
import {useAgent, useSession} from '#/state/session'
import {atoms as a, useBreakpoints, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import {DocumentAudienceChips} from '#/components/documents/DocumentAudience'
import {
  DocumentMaterialRetry,
  DocumentPlayCallout,
  DocumentQuestionCount,
  PageAudioControl,
} from '#/components/documents/DocumentMaterialPlay'
import {DocumentPlayerStrip} from '#/components/documents/DocumentPlayerStrip'
import {DocumentPlaysDialog} from '#/components/documents/DocumentPlaysDialog'
import {PageMaterialBar} from '#/components/documents/PageMaterialSheet'
import {ArrowsDiagonalOut_Stroke2_Corner0_Rounded as ExpandIcon} from '#/components/icons/ArrowsDiagonal'
import {
  ChevronLeft_Stroke2_Corner0_Rounded as ChevronLeftIcon,
  ChevronRight_Stroke2_Corner0_Rounded as ChevronRightIcon,
} from '#/components/icons/Chevron'
import {Pencil_Stroke2_Corner0_Rounded as PencilIcon} from '#/components/icons/Pencil'
import {Loader} from '#/components/Loader'
import {Text} from '#/components/Typography'
import {type PdfCarouselCompression} from './carousel/CompressionChip'
import {CompressionChip} from './carousel/CompressionChip'
import {StageButton, StageControl, StageOverlay} from './carousel/StageChrome'
import {useDocumentPlaySession} from './carousel/useDocumentPlaySession'
import {useStageMaterial} from './carousel/useStageMaterial'
import {PdfFullscreen} from './PdfFullscreen.web'
import {PdfPageCanvas} from './PdfPageCanvas.web'
import {usePagerGestures} from './usePagerGestures.web'

const MIN_STAGE_HEIGHT = 180
const GUTTER = 12
const DOTS_MAX_PAGES = 8

export type PdfCarouselPreview = {
  /** Stable identity for the local file */
  cacheKey: string
  bytes: Uint8Array
}

/*
 * Where the compressor's own type lives now: beside the control that draws it, in
 * `./carousel/CompressionChip` (23 Sep 2026). Re-exported here because the card's props
 * are read from this file.
 */
export type {PdfCarouselCompression} from './carousel/CompressionChip'

export type PdfCarouselProps = {
  embed: DocumentEmbedView
  authorDid: string
  /** Composer preview: render the local file instead of fetching the blob */
  preview?: PdfCarouselPreview
  /** Called when the remove button is pressed (composer only) */
  onRemove?: () => void
  /** Called when the alt text chip is pressed (composer only) */
  onEditAlt?: () => void
  /**
   * Called when the title is edited (composer only — a published document's
   * title belongs to its record and is not editable from the card).
   */
  onEditTitle?: () => void
  /** Composer only: the compression result, shown in place of the plain size */
  compression?: PdfCarouselCompression
  /** Composer only: choose another level, which re-compresses and re-uploads */
  onChangeCompressionLevel?: (level: OptimizeLevel) => void
  /**
   * Who the document was published for. Kept as codes and named here, so the
   * card says it in the reader's language rather than the author's.
   */
  audience?: DocumentAudience
  /** Alt text override, eg. before the post is published */
  altOverride?: string
  /**
   * Where the published document's page material is, and what each page holds.
   *
   * Read from the post's record, so every badge on this card is drawn without a
   * request — the material itself is fetched only when somebody plays (see
   * `useDocumentMaterialQuery`).
   */
  materialRef?: DocumentMaterialRef
  /**
   * Composer only: the material the author has authored so far.
   *
   * Handed in full rather than per page, because a page's material is written
   * against the document as a whole (a re-compress keeps the page numbers), and
   * the docked bar and the badges both read the same model the sheet commits to.
   */
  pageMaterial?: DocumentMaterial
  /**
   * Composer only: called when a page's material is confirmed or cleared.
   *
   * Its presence is what makes this the *author's* carousel: it is what puts the
   * `+` and the docked bar on the page, and what keeps the reader's automatic
   * "this page asks something" card out of the way while they write.
   */
  onChangePageMaterial?: (
    page: number,
    material: DocumentPageMaterial | undefined,
  ) => void
  /**
   * The post this document belongs to, once it has one.
   *
   * Needed for the two things that are about a *published* document: a play's
   * results are attributed to it, and its author's summary is asked for by it.
   */
  postUri?: string
  /** Renders the author-only plays summary. */
  isAuthor?: boolean
  /**
   * Composer preview: questions play here, and nothing is recorded.
   *
   * Derived from `onChangePageMaterial` being absent would be wrong — a published
   * document its author is looking at is neither. This is stated instead.
   */
  isPreview?: boolean
  style?: StyleProp<ViewStyle>
  /**
   * A ceiling on how tall the page may grow.
   *
   * Unbounded by default, which is what makes the page the card: the paper is as
   * tall as its own proportions make it at the width it is given, and nothing is
   * drawn beside it. A surface that needs a short card can cap this and take the
   * page letterboxed in the room that is left.
   */
  maxStageHeight?: number
}

export function PdfCarousel({
  embed,
  authorDid,
  preview,
  onRemove,
  onEditAlt,
  onEditTitle,
  compression,
  onChangeCompressionLevel,
  audience,
  altOverride,
  materialRef,
  pageMaterial,
  onChangePageMaterial,
  postUri,
  isAuthor = false,
  isPreview = false,
  style,
  maxStageHeight = Number.POSITIVE_INFINITY,
}: PdfCarouselProps) {
  const {_} = useLingui()
  const t = useTheme()
  const {currentAccount} = useSession()
  const agent = useAgent()
  const {gtPhone} = useBreakpoints()
  const hasHover = useHasHover()
  usePdfStyles()

  const pageCount = embed.pageCount ?? 0
  const alt = altOverride ?? embed.alt

  /** Whether this carousel is the author writing the material, not reading it. */
  const isComposing = !!onChangePageMaterial

  /*
   * The material: the address it lives at, the fetch the first play starts, and the
   * table every badge on this card is drawn from — all of it in `./carousel/useStageMaterial`,
   * because that derivation is the part of this surface with the longest memory (a
   * recovered address, a post being re-read, a fetch that cannot be enabled until it
   * answers).
   */
  const {
    material,
    materialUri,
    pageTable,
    totals,
    firstQuizPage,
    materialState,
    requestMaterial,
    retryMaterial,
  } = useStageMaterial({
    materialRef,
    pageMaterial,
    isComposing,
    postUri,
    agent,
  })
  /**
   * The page whose material the *reader* should open its editor on, if any.
   *
   * Writing a page's audio and questions moved out of the card and into the full
   * screen reader, which is the only surface with the room for it: a landscape page
   * in a column-width card is a strip of paper, and the editor is a bottom sheet — so
   * the author was writing about a page they could not see, over a page they could not
   * read. Here the sheet opens over a page drawn as large as the window allows, and
   * the author can page through the document writing as they go.
   *
   * The card keeps one door into it (`Add activity`), and this is the page that door
   * should open the editor on — the card's current page, or the page of a bar the
   * author pressed *Edit* on.
   */
  const [startEditorPage, setStartEditorPage] = useState<number | null>(null)
  const source = useMemo<PdfSource>(() => {
    if (preview) {
      return {kind: 'bytes', key: preview.cacheKey, bytes: preview.bytes}
    }
    const headers: Record<string, string> = {}
    if (currentAccount?.accessJwt) {
      headers.Authorization = `Bearer ${currentAccount.accessJwt}`
    }
    return {
      kind: 'remote',
      key: `${authorDid}:${embed.cid}`,
      url: buildDocumentBlobUrl(authorDid, embed.cid),
      headers,
    }
  }, [preview, currentAccount, authorDid, embed.cid])

  const {isNear, ref: nearScreenRef} = useIsNearScreen(true)
  const {status, doc, docKey, numPages, ratio, retry} = usePdfDocument({
    source,
    enabled: isNear,
  })

  /**
   * The document's length in pages, when it is known.
   *
   * What the board measures a read's progress against, and it is taken from the *file*
   * when the file has been parsed — that is the truth about this deck — with the post's
   * own count behind it, so a card whose PDF has not loaded (a document scrolled out of
   * view still says who has played it) ranks its readers against the length the author
   * published rather than against nothing. Nought pages is not a length, so it is left
   * unknown instead of handed on as one.
   */
  const documentPageCount =
    (status === 'ready' ? numPages : pageCount) || undefined

  /*
   * What people have done with this document: the board, the strip's proof, this
   * reader's place in it and the read they are making of it now — all of it in
   * `./carousel/useDocumentPlaySession`, because those three facts answer each other
   * (a board ranks reads against the material, a resume reads this visit's own page,
   * and a take-up is handed that resume) and are one concern for it.
   *
   * The one thing it is told rather than works out is how long the document is: the
   * card has parsed the file, so the card knows.
   */
  const {
    leaderboard,
    playerProof,
    resume,
    playsControl,
    playsWanted,
    openPlays,
    reportReaderPage,
    recordQuizResult,
    flushPlace,
  } = useDocumentPlaySession({
    postUri,
    embedTitle: embed.title,
    documentPageCount,
    publishedPageCount: embed.pageCount,
    firstQuizPage,
    viewerDid: currentAccount?.did,
    isComposing,
    isPreview,
    isNear,
    material,
    pageTable,
  })

  const [page, setPage] = useState(1)
  const [stageWidth, setStageWidth] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [stageHovered, setStageHovered] = useState(false)

  const pageCounts = pageTable
    ? materialPageCounts(pageTable, page)
    : {audio: 0, questions: 0}
  const pageRow = material ? pageMaterialFor(material, page) : undefined

  /**
   * The page somebody asked the reader to open on, and whether the questions are
   * waiting there when it does.
   *
   * A page's questions are answered *between* pages, in the reader — never here.
   * This surface announces: a page asks something, the count says how much, and
   * the press hands the reader to the surface that asks it (see
   * `#/lib/document/quizGate`), where the question card is the door out of the
   * page it belongs to and the following page is read once the door is passed.
   *
   * It is *where to open*, not a mode of this surface: read once, when the full
   * screen reader mounts. A card laid over a page at feed size would be the other
   * thing entirely — a question about a page not yet read, in a column too narrow
   * to read it in.
   */
  const [quizRequest, setQuizRequest] = useState<{
    page: number
    /** Whether the questions are open on arrival, rather than behind the turn. */
    ask: boolean
  } | null>(null)

  /**
   * Open the reader on a page.
   *
   * `ask` is the author's alone: the composer's page bar previews the questions
   * its author has just written, and there is nothing to read before them. A
   * *reader* is taken to the page instead, never to the question card — the
   * questions of a page are its door, and a card in front of a page nobody has
   * looked at yet asks them about something they have not read. So a reader who
   * presses Play lands on the first page that asks anything, reads it, and meets
   * its questions on the way out — which is where they were always meant to be.
   */
  const openQuiz = useCallback(
    (target: number, options?: {ask?: boolean}) => {
      setQuizRequest({page: target, ask: options?.ask ?? false})
      setIsFullscreen(true)
      /*
       * And ask for the material *now*, on the press, rather than when the reader
       * arrives at a page that asks something.
       *
       * The reader is where questions are answered, and a reader who is about to
       * open one is a reader who is about to meet them — usually several seconds
       * after this press, since the first page that asks is read before its
       * questions are put. Asking here spends those seconds on the record's round
       * trip instead of spending them on a spinner over the page, which is what the
       * door used to do: it asked at the moment it needed the answer (see the
       * reader's own request on open, which covers the reader opened to *read*).
       *
       * Asked through the card's start press rather than by writing the flag, which
       * is what makes this the *play* the card offers: that press re-reads the post
       * before it starts, so a document whose material address has moved since the
       * app cached the post is played at the address it lives at now
       * (`requestMaterial` above, which is also one identity for this arrow's deps).
       */
      void requestMaterial()
    },
    [requestMaterial],
  )

  /**
   * Open the reader on a page with its material editor already up.
   *
   * The author's other door into the same surface: questions are *previewed* by one
   * and *written* by this one, and both open the reader on the same page — an author
   * checking what they wrote and an author changing it are looking at one thing.
   */
  const openAuthorEditor = useCallback(
    (target: number) => {
      setStartEditorPage(target)
      setQuizRequest({page: target, ask: false})
      setIsFullscreen(true)
      /*
       * And ask for the material *now*, on the press, rather than when the reader
       * arrives at a page that asks something.
       *
       * The reader is where questions are answered, and a reader who is about to
       * open one is a reader who is about to meet them — usually several seconds
       * after this press, since the first page that asks is read before its
       * questions are put. Asking here spends those seconds on the record's round
       * trip instead of spending them on a spinner over the page, which is what the
       * door used to do: it asked at the moment it needed the answer (see the
       * reader's own request on open, which covers the reader opened to *read*).
       *
       * Asked through the start press for the same reason the play above is, and it
       * is the same press: this author's own card is a composer (`isComposing`), so
       * the read the press would start is the one the composer never makes — the
       * flag it flips is the whole of what happens here.
       */
      void requestMaterial()
    },
    [
      // The press the body calls, and the setter it calls, which the compiler infers
      // as a dependency (see `reportReaderPage`).
      requestMaterial,
      setStartEditorPage,
    ],
  )

  const stageRef = useRef<HTMLDivElement | null>(null)
  const pageRef = useRef<HTMLDivElement | null>(null)
  const dpr = getPdfDpr()

  const totalPages = status === 'ready' ? numPages : pageCount || 1

  /**
   * Where the card is taken when it is told to turn, and *only* that.
   *
   * A turn used to be a door here as well as in the reader: the composer's forward press —
   * and the dots, the wheel, the swipe and the keys with it — stopped at a page whose
   * questions the author had not looked at yet, drew them over the page, and took the turn
   * only once they were closed. It does not any more, and the reason is the role rather
   * than the rule: the questions an author is turning away from are the ones they have just
   * written, on the way to writing the next page, so a turn that raised them was this
   * surface interrupting an author with their own work. The page's own bar wears the press
   * for that (`Preview this page's questions`, which opens this reader on the page with the
   * questions up), and nothing raises them by itself.
   *
   * So the roles are as they should be: a *reader's* turn meets a page's questions
   * (`#/lib/document/quizGate`, walked by `./reader/useQuizDoors`), and an author's turn is
   * a page turn. Both surfaces ask the same gate, and only one of them is a door.
   */
  const goToPage = useCallback((next: number) => {
    setPage(current => (next === current ? current : next))
  }, [])

  usePagerGestures({
    stageRef,
    pageRef,
    page,
    numPages: Math.max(totalPages, 1),
    onPageChange: goToPage,
    enabled: status === 'ready',
  })

  // Keep the current page in range, eg. after a retry or an edit
  useEffect(() => {
    if (status === 'ready' && page > numPages) {
      setPage(numPages)
    }
  }, [status, numPages, page])

  // Warm the neighbouring pages once the visible one has settled. This is what
  // makes swiping feel instant instead of showing a spinner per page.
  useEffect(() => {
    if (status !== 'ready' || !doc || stageWidth <= 0 || totalPages <= 1) return
    const neighbour = page + 1
    const prev = page - 1
    const run = () => {
      for (const candidate of [neighbour, prev]) {
        if (candidate >= 1 && candidate <= totalPages) {
          prefetchPdfPage({
            doc,
            docKey,
            pageNumber: candidate,
            targetWidth: stageWidth,
            dpr,
          })
        }
      }
    }
    const win = window as unknown as {
      requestIdleCallback?: (
        cb: () => void,
        options?: {timeout: number},
      ) => number
      cancelIdleCallback?: (handle: number) => void
    }
    if (win.requestIdleCallback) {
      const handle = win.requestIdleCallback(run, {timeout: 1200})
      return () => win.cancelIdleCallback?.(handle)
    }
    const timeout = setTimeout(run, 250)
    return () => clearTimeout(timeout)
  }, [status, doc, docKey, page, totalPages, stageWidth, dpr])

  /**
   * How big the page is — and with nothing beside it, how big the card's stage is.
   *
   * The page is whatever its own proportions make of the width the card gives it,
   * so the paper spans the card edge to edge and the card's rounded corners are cut
   * into the document rather than into a strip of background. `maxStageHeight` is
   * the way back to a letterboxed page, for a surface that needs a shorter card.
   */
  const layout = useMemo(() => {
    const available = stageWidth
    if (available <= 0) {
      return {
        stageHeight: MIN_STAGE_HEIGHT,
        pageWidth: 0,
        pageHeight: 0,
      }
    }
    const stageHeight = Math.max(
      MIN_STAGE_HEIGHT,
      Math.min(available / ratio, maxStageHeight),
    )
    const pageWidth = Math.min(available, stageHeight * ratio)
    const pageHeight = pageWidth / ratio
    return {stageHeight, pageWidth, pageHeight}
  }, [stageWidth, ratio, maxStageHeight])

  const showControls = !hasHover || stageHovered
  /**
   * Whether the page dots are drawn along the stage's bottom edge.
   *
   * Held as one fact rather than the same comparison twice, because the bottom
   * column has to *make room* for them: the strip that sits there would otherwise
   * finish level with the dots it is telling the reader to ignore.
   */
  const showDots = totalPages > 1 && totalPages <= DOTS_MAX_PAGES
  const canGoBack = page > 1
  const canGoForward = page < totalPages

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      /*
       * The stage pages on the arrow keys, which is exactly what a caret needs — so a
       * field inside the stage keeps them (see `isTextEntry`). Nothing in the stage is a
       * field any more, because a page's material is written in the reader rather than
       * over the card; the guard stays as the one line that keeps a future one from
       * losing its arrows.
       */
      if (isTextEntry(event.target)) return
      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case 'PageDown':
          if (page >= totalPages) return
          event.preventDefault()
          event.stopPropagation()
          goToPage(page + 1)
          break
        case 'ArrowLeft':
        case 'ArrowUp':
        case 'PageUp':
          if (page <= 1) return
          event.preventDefault()
          event.stopPropagation()
          goToPage(page - 1)
          break
        // Space/Enter deliberately do nothing here: the carousel pages in
        // place, and the full screen reader opens only from its expand control
        // (which is reachable by tabbing).
      }
    },
    [goToPage, page, totalPages],
  )

  const stageAriaLabel = embed.title
    ? _(msg`${embed.title}, page ${page} of ${totalPages}`)
    : _(msg`Document, page ${page} of ${totalPages}`)

  return (
    <View
      ref={nearScreenRef}
      style={[
        a.mt_sm,
        a.rounded_md,
        a.overflow_hidden,
        a.border,
        {
          borderColor: t.palette.contrast_200,
          backgroundColor: t.atoms.bg.backgroundColor,
        },
        style,
      ]}>
      {/*
        Interaction island: keeps clicks and drags inside the carousel from
        triggering the post link that wraps every embed.
      */}
      <div
        onClick={event => event.stopPropagation()}
        onPointerEnter={() => setStageHovered(true)}
        onPointerLeave={() => setStageHovered(false)}
        style={{position: 'relative'}}>
        {/*
          The frame the page fills.

          This used to be a grey panel with a gutter of its own, which drew a
          border around the paper — the card's rounded corners cut into a strip of
          background rather than into the document. Now it is only the measure:
          whatever width the card hands it is the width the page gets.
        */}
        <View
          style={[a.w_full, {backgroundColor: '#ffffff'}]}
          onLayout={event => setStageWidth(event.nativeEvent.layout.width)}>
          <div
            ref={stageRef}
            role="group"
            aria-roledescription={_(msg`document carousel`)}
            aria-label={stageAriaLabel}
            /*
              The alt text stays on the stage even though it is no longer drawn
              on it: the strip took the band it used to fill, and a document
              whose description vanished from the card would be a document a
              screen reader cannot describe.
            */
            aria-description={alt || undefined}
            tabIndex={0}
            onKeyDown={onKeyDown}
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: layout.stageHeight,
              touchAction: 'pan-y',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              outlineOffset: 2,
            }}>
            <div ref={pageRef} style={{willChange: 'transform'}}>
              {status === 'ready' && doc && layout.pageWidth > 0 ? (
                <PdfPageCanvas
                  doc={doc}
                  docKey={docKey}
                  pageNumber={page}
                  width={layout.pageWidth}
                  height={layout.pageHeight}
                  dpr={dpr}
                />
              ) : (
                <View
                  style={{
                    width: Math.max(layout.pageWidth, 1),
                    height: Math.max(layout.pageHeight, 1),
                    backgroundColor: '#ffffff',
                  }}>
                  <div className={PDF_SKELETON_CLASS} />
                </View>
              )}
            </div>

            {status === 'loading' && (
              <StageOverlay>
                <Loader size="md" />
                <Text style={[a.text_xs, {color: '#fff'}]}>
                  {_(msg`Loading document…`)}
                </Text>
              </StageOverlay>
            )}

            {status === 'error' && (
              <StageOverlay>
                <Text style={[a.text_xs, {color: '#fff'}]}>
                  {_(msg`Could not load this document`)}
                </Text>
                <Button
                  label={_(msg`Try again`)}
                  onPress={() => retry()}
                  size="small"
                  variant="solid"
                  color="primary"
                  shape="default">
                  <Text style={[a.text_xs, a.font_bold, {color: '#fff'}]}>
                    {_(msg`Try again`)}
                  </Text>
                </Button>
              </StageOverlay>
            )}

            {showControls && canGoBack && (
              <StageButton
                side="left"
                label={_(msg`Previous page`)}
                onPress={() => goToPage(page - 1)}>
                <ChevronLeftIcon size="lg" fill="#fff" />
              </StageButton>
            )}
            {showControls && canGoForward && (
              <StageButton
                side="right"
                label={_(msg`Next page`)}
                onPress={() => goToPage(page + 1)}>
                <ChevronRightIcon size="lg" fill="#fff" />
              </StageButton>
            )}

            {/*
              What *this page* carries, in the stage's top-left corner.

              It is a corner the page itself does not use: the page's own start is
              the top-left of the paper, and while a document is being written that
              is where the eye lands first — so a page that already asks something
              says so there, beside the document's own controls opposite, rather
              than under the page in the row this surface keeps for reading it.
              Drawn on the film every control over a page wears, because the paper
              is white in both modes and a light bar on it would vanish.
            */}
            {isComposing && pageCounts.audio + pageCounts.questions > 0 && (
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  maxWidth: 'calc(100% - 16px)',
                }}>
                <PageMaterialBar
                  counts={pageCounts}
                  // The author's own questions, opened on the spot: this is a
                  // preview of what was just written, not a reader's turn.
                  onPreview={() => openQuiz(page, {ask: true})}
                  onEdit={() => openAuthorEditor(page)}
                />
              </div>
            )}

            {status === 'ready' && (
              <div
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 8,
                }}>
                {onRemove && (
                  <StageControl
                    label={_(msg`Remove document`)}
                    onPress={onRemove}>
                    <FontAwesomeIcon icon="xmark" size={16} color="#ffffff" />
                  </StageControl>
                )}
                {/*
                  The page's clip joins the controls the stage already keeps
                  here, rather than floating somewhere of its own: a reader
                  learns one corner for "what can I do with this page".
                */}
                {!isComposing && pageCounts.audio > 0 && (
                  <PageAudioControl
                    audioUrl={pageRow?.audioUrl}
                    /*
                     * Pending while the read is *coming*, and not while it has ended —
                     * the rule the reader's own copy of this control follows, and for the
                     * reason the two states are told apart at all (`documentMaterialState`):
                     * a control that spins over a read that is over is a lie, and its press
                     * cannot start anything, so a failed read left the reader with a spinner
                     * that never stopped and a press that did nothing.
                     *
                     * `unasked` stays pressable, which is where this differs from the reader:
                     * there, opening the reader *is* the ask, so a page's clip is honestly
                     * still coming. Here the press is what starts the fetch, and it is the
                     * only way in the control has ever had.
                     */
                    isPending={materialState === 'asking'}
                    onRequest={
                      materialState === 'failed'
                        ? () => {
                            void retryMaterial()
                          }
                        : requestMaterial
                    }
                  />
                )}
                {isComposing && pageCounts.audio > 0 && pageRow?.audioUrl && (
                  <PageAudioControl audioUrl={pageRow.audioUrl} />
                )}
                <StageControl
                  label={_(msg`Open document full screen`)}
                  onPress={() => setIsFullscreen(true)}>
                  <ExpandIcon size="md" fill="#fff" />
                </StageControl>
              </div>
            )}

            {showDots && (
              <div
                style={{
                  position: 'absolute',
                  bottom: 6,
                  left: 0,
                  right: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  gap: 5,
                }}>
                {Array.from({length: totalPages}).map((_, index) => (
                  <div
                    key={index}
                    onClick={event => {
                      event.stopPropagation()
                      goToPage(index + 1)
                    }}
                    style={{
                      width: index + 1 === page ? 7 : 6,
                      height: index + 1 === page ? 7 : 6,
                      borderRadius: 99,
                      backgroundColor:
                        index + 1 === page
                          ? t.palette.contrast_950
                          : t.palette.contrast_400,
                      opacity: index + 1 === page ? 0.9 : 0.55,
                      cursor: 'pointer',
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </View>

        {/*
          The stage's bottom column: the author's own page furniture, then who has
          played it. One column rather than several absolutely-placed bars, so the
          strip sits above the author's own page furniture instead of the two
          stacking on top of each other.

          A page's *material* is no longer in here: what a page carries is said in
          the stage's top-left corner, where the page starts and where an author
          writing it is already looking.

          It is on the paper, not under it: the faces are evidence about *this*
          document, and the document is the page the reader is looking at — the
          card's footer is where its title and its offer live, and a row of
          strangers' avatars is neither.
        */}
        <div
          style={{
            position: 'absolute',
            left: 12,
            right: 12,
            // The dots keep the stage's bottom edge to themselves: the strip sits
            // above them, clear of the one row it would otherwise finish level
            // with.
            bottom: GUTTER + (showDots ? 10 : 0),
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            gap: 8,
          }}>
          {/*
            The author's one press for a page's activity, in the corner the stage
            keeps for what *this page* carries — and the only way into writing it.

            It is a word rather than a `+`, because what it opens is not a field but
            a place to work: the reader, at the size of the window, with a `+` of its
            own for the page in front of whoever is writing.
          */}
          {isComposing && (
            <View style={[a.flex_row, a.justify_end]}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={_(msg`Add activity`)}
                accessibilityHint={_(
                  msg`Opens the page full screen, where its audio and questions are written`,
                )}
                onPress={() => openAuthorEditor(page)}
                testID="documentAddActivity"
                style={({pressed}) => [
                  styles.addActivity,
                  {opacity: pressed ? 0.8 : 1},
                ]}>
                <Feather name="plus" size={16} color="#fff" />
                <Text style={[a.text_xs, a.font_bold, {color: '#fff'}]}>
                  <Trans>Add activity</Trans>
                </Text>
              </Pressable>
            </View>
          )}
          {isComposing && onEditAlt && (
            <View style={[a.flex_row, a.justify_start]}>
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={
                  alt ? _(msg`Edit alt text`) : _(msg`Add alt text`)
                }
                accessibilityHint={_(msg`Opens the alt text editor`)}
                onPress={onEditAlt}
                testID="documentAltText"
                style={({pressed}) => [
                  styles.altBar,
                  {opacity: pressed ? 0.8 : 1},
                ]}>
                <Text style={[a.text_xs, {color: '#fff'}]} numberOfLines={2}>
                  {alt || _(msg`Add alt text`)}
                </Text>
              </Pressable>
            </View>
          )}
          {playerProof && (
            <DocumentPlayerStrip proof={playerProof} onOpen={openPlays} />
          )}
        </div>

        {/*
          The page's material editor, over the page it is about.

          It is rendered inside the *stage* rather than in a dialog so the author
          can see the page they are writing for — the whole reason the material
          lives on a page in the first place. Confirm commits to the composer;
          nothing is written until it is pressed.
        */}
      </div>

      {/*
        The footer, as a strip with a top rather than a bottom.

        It opens with the reading's progress and then the document's own line: the
        title, the size it took, and — at the end of that line, where a row of
        counts used to sit — the one press that plays it. The line under it says
        what the document asks, in words; the line under *that* says who the
        document is for and how far in the reader is: what a reader is asked to
        *do* leads the strip, and the page they are on sits on the edge it is a
        measure of.

        Who has played it is deliberately *not* here. The faces belong on the
        paper, beside the document they are about — see the stage's bottom column.
      */}
      <View style={{borderTopWidth: 1, borderTopColor: t.palette.contrast_100}}>
        <View
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          style={[
            a.w_full,
            {height: 3, backgroundColor: t.palette.contrast_100},
          ]}>
          <View
            style={{
              height: 3,
              width: `${Math.max(4, Math.round((page / Math.max(totalPages, 1)) * 100))}%`,
              backgroundColor: t.palette.primary_500,
            }}
          />
        </View>

        <View style={[a.px_md, a.py_sm, a.gap_xs]}>
          <View style={[a.flex_row, a.align_center, a.gap_sm]}>
            {onEditTitle && (
              <Button
                label={_(msg`Edit title`)}
                onPress={onEditTitle}
                size="tiny"
                variant="ghost"
                color="secondary"
                shape="round">
                <ButtonIcon icon={PencilIcon} size="xs" />
              </Button>
            )}

            <Text
              style={[a.flex_1, a.text_sm, a.font_bold, t.atoms.text]}
              numberOfLines={1}>
              {embed.title ?? _(msg`Document`)}
            </Text>

            {compression && onChangeCompressionLevel ? (
              <CompressionChip
                compression={compression}
                onChangeLevel={onChangeCompressionLevel}
              />
            ) : gtPhone && embed.fileSize ? (
              <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
                {formatFileSize(embed.fileSize)}
              </Text>
            ) : null}

            {/*
              The press that plays the whole document: from the first page that asks
              anything, through every page that follows.

              It stands where the material badges used to — at the end of the title
              line — because this, and not a count of what is inside, is what the
              card is offering the reader. It belongs to the document rather than to
              the page below, so it is here on every page of a book that asks
              anything, and absent from one that asks nothing.
            */}
            {!isComposing &&
              firstQuizPage !== undefined &&
              /*
               * A read that failed is said *here*, where the offer was, rather than left to
               * the reader: this card is what starts the material's fetch, so a document whose
               * material could not be read must not go on offering a press that opens onto
               * nothing. Same one line, same deliberate press — the pill asks again.
               */
              (materialState === 'failed' ? (
                <DocumentMaterialRetry
                  onRetry={() => {
                    void retryMaterial()
                  }}
                />
              ) : (
                <DocumentPlayCallout
                  count={totals.questions}
                  resumeFrom={resume?.page}
                  counts={resume?.counts}
                  onPlay={() => openQuiz(resume?.page ?? firstQuizPage)}
                />
              ))}
          </View>

          {/*
            What the document asks, said in words, directly under its title.

            The press is the offer; this is the sentence that says what the offer
            is. It reads as a caption under the document's own name rather than as
            one more badge on the row below, which is where the counts used to be.
          */}
          {!isComposing && firstQuizPage !== undefined && (
            <DocumentQuestionCount
              count={totals.questions}
              resumeFrom={resume?.page}
              counts={resume?.counts}
            />
          )}
        </View>
      </View>

      <Text
        style={[
          a.text_xs,
          {position: 'absolute', opacity: 0, height: 1, width: 1},
        ]}>
        {plural(totalPages, {
          one: '# page',
          other: '# pages',
        })}
      </Text>

      {/*
        The document's own line, under the title: who it is for, and where the
        reader is inside it.

        The page counter used to close the title row, where it was the last of a
        row of numbers and read as one more count of the deck's contents. Here it
        sits with the subject and the level, and says the one thing those chips and
        it have in common: where you are.
      */}
      {(audience || totalPages > 1) && (
        <View
          style={[
            a.px_md,
            a.pb_sm,
            a.flex_row,
            a.align_center,
            a.flex_wrap,
            a.gap_xs,
          ]}>
          {audience && <DocumentAudienceChips audience={audience} />}
          {totalPages > 1 && (
            <View
              style={[
                a.px_xs,
                a.py_2xs,
                a.rounded_xs,
                {backgroundColor: t.palette.contrast_50},
              ]}>
              <Text
                style={[a.text_2xs, a.font_bold, t.atoms.text_contrast_medium]}>
                {page} / {totalPages}
              </Text>
            </View>
          )}
        </View>
      )}

      {postUri && (isAuthor || leaderboard.length > 0) && (
        <DocumentPlaysDialog
          control={playsControl}
          documentUri={postUri}
          title={embed.title}
          materialUri={materialUri}
          pageTable={pageTable}
          pageCount={documentPageCount}
          enabled={playsWanted}
          isAuthor={isAuthor}
          viewerDid={currentAccount?.did}
          /*
            The leaderboard's own press plays the document the way the card's
            does — at the page this reader left off on, when there is one — so a
            reader who came here to look at the board is not sent back to the
            beginning by the button at the bottom of it.
          */
          onPlay={
            firstQuizPage !== undefined
              ? () => {
                  playsControl.close()
                  openQuiz(resume?.page ?? firstQuizPage)
                }
              : undefined
          }
        />
      )}

      {isFullscreen && (
        <PdfFullscreen
          embed={embed}
          authorDid={authorDid}
          preview={preview}
          // Somebody who asked to play opens *on* the first page that asks, not on
          // the one the carousel happens to be showing — and reads it before its
          // questions are put to them.
          initialPage={quizRequest?.page ?? page}
          numPagesHint={totalPages}
          audience={audience}
          material={material}
          pageTable={pageTable}
          onRequestMaterial={requestMaterial}
          /*
           * The reader's own retry — a press rather than a fact, so this arrow is rebuilt on
           * every render of the card and that costs nothing: the reader holds the latest one
           * through a stable wrapper (`useNonReactiveCallback`), and only presses call it.
           */
          onRetryMaterial={() => {
            void retryMaterial()
          }}
          materialState={materialState}
          startQuizPage={quizRequest?.ask ? quizRequest.page : undefined}
          isPreview={isComposing || isPreview}
          /*
           * The author's editor, handed to the reader rather than drawn here.
           *
           * Present means "this reader is a document being written": the reader grows
           * the `+` that opens a page's material and the bar that counts it, and an
           * author who came here to write is not asked to learn a second place to do
           * it. Absent for every reader of a published document, which is what keeps
           * the writing chrome out of their copy.
           */
          onChangePageMaterial={onChangePageMaterial}
          startEditorPage={startEditorPage ?? undefined}
          onQuizFinished={recordQuizResult}
          onPageSeen={reportReaderPage}
          onClose={() => {
            setIsFullscreen(false)
            setQuizRequest(null)
            /*
             * The author's two requests are *ways in*, not state the reader holds:
             * a reader left open on a page with its editor up must not open the
             * editor again the next time somebody presses *Add activity* on a
             * different page.
             */
            setStartEditorPage(null)
            /*
             * Leaving the reader writes the place rather than waiting out the
             * timer: the page a reader closed the reader on is the page they will
             * look for next time, and a timer that never fires would lose it.
             */
            flushPlace()
          }}
        />
      )}
    </View>
  )
}

/**
 * Composer-only control for the compressor, standing in for the size readout:
 * the number is what the post will upload, and the saving beside it is the
 * point of the feature. Changing the level re-compresses the picked file and
 * uploads the result, so the menu doubles as the explanation.
 */
const styles = StyleSheet.create({
  /**
   * The author's alt text, where the page is: a scrim over the paper, the same
   * dark pill the stage's controls wear.
   *
   * The author keeps this band about the document's own description — it is the
   * press that opens the alt text editor, and a composer with no way to reach it
   * from the card would be a composer that hides half of writing the post. A
   * *reader* sees the players strip here instead; the two never appear together,
   * because a document being written has nobody's plays to show.
   */
  altBar: {
    maxWidth: '100%',
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 7,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
    cursor: 'pointer',
  },
  /**
   * The composer's one press for a page's activity, at the stage's bottom-right.
   *
   * A labelled pill rather than a bare `+` in the corner: it is the author's whole
   * route to writing a page, and a word is what tells somebody who has never had a
   * document with questions what the document is for. It wears the same dark film as
   * every other control drawn over a page, because the page behind it is white paper.
   */
  addActivity: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderRadius: 99,
    paddingHorizontal: 14,
    paddingVertical: 9,
    backgroundColor: 'rgba(0, 0, 0, 0.62)',
    cursor: 'pointer',
  },
})
